import { useState, useEffect, useCallback, useRef } from 'react';
import { checkThresholds, getNotifications, markNotificationRead, markAllNotificationsRead } from '../api';

const POLL_INTERVAL = 30000; // 30 seconds

export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newBreaches, setNewBreaches] = useState([]);
  const permissionRef = useRef('default');

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(perm => {
        permissionRef.current = perm;
      });
    } else if ('Notification' in window) {
      permissionRef.current = Notification.permission;
    }
  }, []);

  const showBrowserNotification = useCallback((notif) => {
    if ('Notification' in window && permissionRef.current === 'granted') {
      try {
        const n = new Notification(notif.title, {
          body: notif.message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: `threshold-${notif.site}-${notif.metric}`,
          requireInteraction: notif.severity === 'critical',
        });
        setTimeout(() => n.close(), 8000);
      } catch {
        // SW notifications not available in this context
      }
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await getNotifications(null, false);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // silently fail
    }
  }, []);

  const runCheck = useCallback(async () => {
    try {
      const { data } = await checkThresholds();
      if (data.new_notifications && data.new_notifications.length > 0) {
        setNewBreaches(data.new_notifications);
        // Show browser notifications
        data.new_notifications.forEach(showBrowserNotification);
        // Refetch full list
        await fetchNotifications();
      }
    } catch {
      // silently fail
    }
  }, [fetchNotifications, showBrowserNotification]);

  // Initial fetch + start polling
  useEffect(() => {
    fetchNotifications();
    runCheck();

    const interval = setInterval(() => {
      runCheck();
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchNotifications, runCheck]);

  const markRead = useCallback(async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silently fail
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // silently fail
    }
  }, []);

  const dismissToast = useCallback((id) => {
    setNewBreaches(prev => prev.filter(n => n._id !== id));
  }, []);

  const dismissAllToasts = useCallback(() => {
    setNewBreaches([]);
  }, []);

  return {
    notifications,
    unreadCount,
    newBreaches,
    markRead,
    markAllRead,
    dismissToast,
    dismissAllToasts,
    refetch: fetchNotifications,
  };
}
